import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Col, Popover, Row, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useGetCountries } from 'api/countries/use-get-countries.ts';
import type { ClientRes, IGetCargoRes } from 'api/swagger';
import { UserRole, useRoleGuard } from 'shared/hooks/use-role-guard.ts';
import { useTransformToTableData } from 'shared/hooks/use-transform-to-table-data';
import { getCargoTypeLabel } from 'shared/utils/cargos/cargo-type-labels.ts';
import { findCountriesById } from 'shared/utils/countries/find-countries-by-id.ts';
import { isFullyCompleted, isFullyLoaded } from 'shared/utils/statuses.ts';
import { formatDate } from 'shared/utils/time/format-date.ts';
import { OrderStatus } from 'types/order.ts';
import { CargoAddressCol } from 'components/cargos/cargo-address-col';
import { CargoInfoCol } from 'components/cargos/cargo-info-col';
import { CargoItemStatuses } from 'components/cargos/cargo-item-status';
import { getWidthCalculator } from 'components/cargos/cargos-list/lib.ts';
import { CargosSummary } from 'components/cargos/cargos-summary';
import { ColorCircles } from 'components/common/color-circles';
import { CargoMapPreview } from '../cargo-map-preview';
import styles from './cargos-list.module.scss';
import { CargoCheckbox } from './components/cargo-checkbox';
import { CargoItemsTable } from './components/cargo-items-table';
import type { ICargoListProps } from './types';

export const CargosList: React.FC<ICargoListProps> = (props) => {
  const {
    cargos,
    isLoading,
    withoutOrder = false,
    withoutClient = false,
    withoutSelect = false,
    withoutExpandable = false,
    withoutSummary = false,
  } = props;
  const { t } = useTranslation();
  const { data: countries } = useGetCountries();
  const tableData = useTransformToTableData(cargos);

  const allowSelectItems = useRoleGuard([UserRole.admin, UserRole.assistant, UserRole.transport_manager]);

  const getWidth = getWidthCalculator(withoutOrder, withoutClient);

  const columns: ColumnsType<IGetCargoRes> = [
    ...(!withoutSelect && allowSelectItems
      ? [
          {
            title: '',
            dataIndex: 'id',
            width: '34px',
            className: styles.checkboxCol,
            render: (_: number, record: IGetCargoRes) => <CargoCheckbox cargo={record} />,
          },
        ]
      : []),
    {
      title: 'ID',
      dataIndex: 'id',
      width: getWidth('id'),
      render: (id: number, { loadingAddress, unloadingAddress, compositeId, type, statuses, client, order }) => {
        const loadingCountry = findCountriesById(loadingAddress.countryId, countries);
        const unloadingCountry = findCountriesById(unloadingAddress.countryId, countries);

        const hasLoadedOrPlannedStatus = statuses.some(
          (status) => status.type === 'loaded' || status.type === 'planned',
        );

        return (
          <Space align='start' size='small' direction='vertical'>
            {client?.isVip && <Tag color='success'>{t('client:type_vip')}</Tag>}
            <Row align='middle' justify='start' gutter={[8, 0]}>
              <Col>
                <Link to={`/cargos/${id}`}>{compositeId}</Link>
              </Col>
              <Col style={{ display: 'flex', alignItems: 'center' }}>
                <ColorCircles cargoId={id} grayCircleClassnames={styles.grayDot} />
              </Col>
            </Row>
            <Row align='middle' justify='space-between' gutter={6}>
              <Col>
                <Tag style={{ margin: 0 }}>{getCargoTypeLabel(type)}</Tag>
              </Col>
              <Col>
                <Typography.Text className={styles.secondary}>
                  {loadingCountry?.code} <ArrowRightOutlined className={styles.arrow} /> {unloadingCountry?.code}
                </Typography.Text>
              </Col>

              <Col>
                {hasLoadedOrPlannedStatus ? (
                  <CargoMapPreview cargoId={id} clientOrderNumber={order.clientOrderNumber} />
                ) : null}
              </Col>
            </Row>
          </Space>
        );
      },
    },
    ...(!withoutOrder
      ? [
          {
            title: 'Order',
            dataIndex: 'orderId',
            width: getWidth('order'),
            render: (orderId: number, { order }: IGetCargoRes) => {
              return (
                <>
                  <Link to={`/orders/${orderId}`} className={styles.secondary}>
                    {order.compositeId}
                  </Link>
                  <Typography.Text className={styles.secondary}>{order.clientOrderNumber}</Typography.Text>
                </>
              );
            },
          },
        ]
      : []),

    {
      title: t('common:status'),
      dataIndex: 'status',
      width: getWidth('status'),
      render: (_, record) => {
        return (
          <>
            <CargoItemStatuses statuses={record.statuses} assetIsReturned={record.assetIsReturned} />
            {record.deliveryDate && isFullyCompleted(record.statuses) ? (
              <div className={styles.date}>{formatDate(record.deliveryDate)}</div>
            ) : null}
          </>
        );
      },
    },
    {
      title: 'Info',
      dataIndex: 'totalWeight',
      width: getWidth('info'),
      render: (_, cargo) => {
        return <CargoInfoCol cargo={cargo} />;
      },
    },
    {
      title: 'Loading',
      dataIndex: 'loadingAddress',
      width: getWidth('loading'),
      render: (
        _,
        {
          loadingAddress,
          loadingCustomAddress,
          loadingDateFrom,
          loadingDateTo,
          statuses,
          documents,
          order,
          pickupDate,
        },
      ) => {
        return (
          <CargoAddressCol
            isOverdueVisible={isFullyLoaded(statuses)}
            needCalculateDate={!isFullyLoaded(statuses) && order.status !== OrderStatus.cancelled}
            address={loadingAddress}
            customAddress={loadingCustomAddress}
            dateFrom={loadingDateFrom}
            dateTo={loadingDateTo}
            documents={documents}
            actualDate={pickupDate}
          />
        );
      },
    },
    {
      title: 'Unloading',
      dataIndex: 'unloadingAddress',
      width: getWidth('unloading'),
      render: (
        _,
        {
          unloadingAddress,
          unloadingCustomAddress,
          unloadingDateFrom,
          unloadingDateTo,
          statuses,
          order,
          deliveryDate,
          cmrReceived,
        },
      ) => {
        const isFullyCompletedStatus = isFullyCompleted(statuses);
        return (
          <CargoAddressCol
            isOverdueVisible={isFullyCompletedStatus}
            needCalculateDate={!isFullyCompletedStatus && order.status !== OrderStatus.cancelled}
            address={unloadingAddress}
            customAddress={unloadingCustomAddress}
            dateFrom={unloadingDateFrom}
            dateTo={unloadingDateTo}
            actualDate={deliveryDate}
            showCmrWarning={!cmrReceived && isFullyCompletedStatus}
          />
        );
      },
    },
    ...(!withoutClient
      ? [
          {
            title: t('client:client'),
            dataIndex: 'client',
            width: getWidth('client'),
            ellipsis: true,
            render: (client: ClientRes, { user, order }: IGetCargoRes) => {
              return (
                <>
                  <Popover
                    content={
                      <>
                        <div>VAT: {client?.vatNumber}</div>
                        <div>Type: {client?.type}</div>
                        <div>
                          Payment: {client?.paymentTerms} - {client?.paymentPeriod}
                        </div>
                      </>
                    }
                    title={client?.name}
                    trigger='click'>
                    <Typography.Text>{client?.shortName || client?.name}</Typography.Text>
                  </Popover>
                  <div className={styles.secondary}>{user.name}</div>
                  <div className={styles.secondary}>{formatDate(order.createdAt)}</div>
                </>
              );
            },
          },
        ]
      : []),
  ];

  const expandedRowRender = (record: IGetCargoRes) => (
    <CargoItemsTable cargo={record} withoutCheckbox={withoutSelect} />
  );

  return (
    <>
      {!withoutSummary && <CargosSummary />}
      <Table
        rowClassName={(record) => (record.order.status === OrderStatus.cancelled ? styles.canceledRow : '')}
        expandable={withoutExpandable ? {} : { expandedRowRender }}
        className={styles.table}
        size={'small'}
        columns={columns}
        dataSource={tableData}
        pagination={false}
        loading={isLoading ? { size: 'large', delay: 200 } : false}
        bordered
      />
    </>
  );
};
